import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowRight, 
  Sparkles,
  Coffee,
  Beer,
  Wine,
  Droplets,
  TrendingUp,
  Shield,
  Zap,
  Globe
} from 'lucide-react';
import toast from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';

const LoginContent = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeFeature, setActiveFeature] = useState(0);
  
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 20,
        y: (e.clientY / window.innerHeight) * 20
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(email, password);
    if (result.success) {
      navigate('/dashboard');
    }
    
    setLoading(false);
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    const result = await googleLogin(credentialResponse.credential);
    if (result.success) {
      navigate('/dashboard');
    }
  };

  const handleGoogleError = () => {
    toast.error('Google login failed. Please try again.');
  };

  const features = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Real-time Analytics",
      description: "Track sales, inventory, and performance metrics in real-time"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Secure Platform",
      description: "Enterprise-grade security for your business data"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Lightning Fast",
      description: "Optimized performance for seamless operations"
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Multi-location",
      description: "Manage multiple depots and sales agents effortlessly"
    }
  ];

  const beverageIcons = [
    { Icon: Coffee, color: "from-amber-500 to-orange-600", delay: 0 },
    { Icon: Beer, color: "from-yellow-500 to-amber-600", delay: 0.2 },
    { Icon: Wine, color: "from-red-500 to-rose-600", delay: 0.4 },
    { Icon: Droplets, color: "from-blue-500 to-cyan-600", delay: 0.6 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 flex items-center justify-center p-4 relative overflow-hidden">
      
      {/* Animated background grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.1) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
          transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`
        }} />
      </div>

      {/* Floating beverage icons */}
      {beverageIcons.map(({ Icon, color, delay }, index) => (
        <div
          key={index}
          className={`absolute animate-float`}
          style={{
            left: `${15 + index * 20}%`,
            top: `${20 + index * 15}%`,
            animationDelay: `${delay}s`,
            opacity: 0.1
          }}
        >
          <Icon className={`w-16 h-16 text-white`} />
        </div>
      ))}

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 8s ease infinite;
        }
        @keyframes pulse-glow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        .animate-pulse-glow {
          animation: pulse-glow 3s ease-in-out infinite;
        }
      `}</style>

      <div className="relative w-full max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          
          {/* Left side - Modern glass morphism card */}
          <div className="relative group h-full">
            {/* Animated gradient border */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 rounded-3xl opacity-75 group-hover:opacity-100 blur-xl group-hover:blur-2xl transition-all duration-1000 animate-gradient"></div>
            
            <div className="relative h-full bg-gray-900/90 backdrop-blur-xl rounded-3xl p-8 lg:p-12 flex flex-col border border-gray-800/50 overflow-hidden">
              
              {/* Glass reflection effect */}
              <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/5 to-transparent pointer-events-none"></div>
              
              {/* Logo and branding */}
              <div className="relative z-10 mb-8 lg:mb-12">
                <div className="flex items-center gap-4 mb-6">
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center transform rotate-3 hover:rotate-6 transition-transform duration-300">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900 animate-pulse"></div>
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">
                      BevFlow
                    </h1>
                    <p className="text-sm text-gray-400">Intelligent Distribution Platform</p>
                  </div>
                </div>

                {/* Dynamic feature showcase */}
                <div className="relative h-32 mb-8">
                  {features.map((feature, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-all duration-700 transform ${
                        index === activeFeature
                          ? 'opacity-100 translate-x-0'
                          : 'opacity-0 translate-x-8 pointer-events-none'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-xl bg-gradient-to-br ${
                          index === 0 ? 'from-blue-500/20 to-cyan-500/20' :
                          index === 1 ? 'from-purple-500/20 to-pink-500/20' :
                          index === 2 ? 'from-amber-500/20 to-orange-500/20' :
                          'from-emerald-500/20 to-teal-500/20'
                        }`}>
                          <div className={`text-${
                            index === 0 ? 'blue' :
                            index === 1 ? 'purple' :
                            index === 2 ? 'amber' :
                            'emerald'
                          }-400`}>
                            {feature.icon}
                          </div>
                        </div>
                        <div>
                          <h3 className="text-white font-semibold mb-1">{feature.title}</h3>
                          <p className="text-sm text-gray-400">{feature.description}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Feature indicators */}
                <div className="flex gap-2">
                  {features.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveFeature(index)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${
                        index === activeFeature
                          ? 'w-8 bg-gradient-to-r from-purple-400 to-pink-400'
                          : 'w-4 bg-gray-600 hover:bg-gray-500'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Stats grid */}
              <div className="relative z-10 grid grid-cols-2 gap-4 mt-auto">
                {[
                  { label: 'Can Process Active Users Over', value: '10k+', change: '+25%' },
                  { label: 'Transactions', value: '500k+', change: '+40%' },
                  { label: 'Response Time', value: '<100ms', change: '-30%' },
                  { label: 'Uptime', value: '99.9%', change: '+0.5%' }
                ].map((stat, index) => (
                  <div key={index} className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-gray-700/50 hover:border-purple-500/30 transition-all duration-300 group">
                    <p className="text-xs text-gray-400 mb-1">{stat.label}</p>
                    <p className="text-xl font-bold text-white mb-1">{stat.value}</p>
                    <p className={`text-xs ${
                      stat.change.startsWith('+') ? 'text-green-400' : 'text-red-400'
                    }`}>{stat.change}</p>
                  </div>
                ))}
              </div>

              {/* Decorative elements */}
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-gradient-to-tl from-purple-600/20 to-pink-600/20 rounded-full filter blur-3xl animate-pulse-glow"></div>
              <div className="absolute top-0 left-0 w-48 h-48 bg-gradient-to-br from-blue-600/20 to-cyan-600/20 rounded-full filter blur-3xl animate-pulse-glow" style={{ animationDelay: '1s' }}></div>
            </div>
          </div>

          {/* Right side - Login form */}
          <div className="relative group h-full">
            {/* Animated gradient border */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 rounded-3xl opacity-75 group-hover:opacity-100 blur-xl group-hover:blur-2xl transition-all duration-1000 animate-gradient"></div>
            
            <div className="relative h-full bg-gray-900/90 backdrop-blur-xl rounded-3xl p-8 lg:p-12 flex items-center border border-gray-800/50">
              
              {/* Glass reflection effect */}
              <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/5 to-transparent pointer-events-none"></div>
              
              <div className="w-full max-w-md mx-auto relative z-10">
                {/* Header */}
                <div className="text-center mb-8 lg:mb-10">
                  <h2 className="text-3xl lg:text-4xl font-bold text-white mb-3">
                    LOGIN
                  </h2>
                  <p className="text-gray-400 text-sm lg:text-base">
                    Sign in to access your dashboard
                  </p>
                </div>
                
                <form className="space-y-5 lg:space-y-6" onSubmit={handleSubmit}>
                  {/* Email field */}
                  <div className="space-y-2">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300">
                      Email address
                    </label>
                    <div className="relative group/input">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-500 group-focus-within/input:text-purple-400 transition-colors" />
                      </div>
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="block w-full pl-12 pr-4 py-3.5 bg-gray-800/50 border border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                        placeholder="you@example.com"
                      />
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/0 via-purple-500/0 to-purple-500/0 group-focus-within/input:from-purple-500/10 group-focus-within/input:via-transparent group-focus-within/input:to-transparent pointer-events-none transition-all duration-500"></div>
                    </div>
                  </div>

                  {/* Password field */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                        Password
                      </label>
                      <a 
                        href="/auth/forgot-password" 
                        className="text-sm text-purple-400 hover:text-purple-300 transition-colors"
                      >
                        Forgot password?
                      </a>
                    </div>
                    <div className="relative group/input">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-gray-500 group-focus-within/input:text-purple-400 transition-colors" />
                      </div>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="block w-full pl-12 pr-12 py-3.5 bg-gray-800/50 border border-gray-700 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 text-white placeholder-gray-500"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-purple-400 transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/0 via-purple-500/0 to-purple-500/0 group-focus-within/input:from-purple-500/10 group-focus-within/input:via-transparent group-focus-within/input:to-transparent pointer-events-none transition-all duration-500"></div>
                    </div>
                  </div>

                  {/* Sign in button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3.5 px-4 rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 focus:ring-offset-gray-900 transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base relative overflow-hidden group/btn"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000"></span>
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Signing in...</span>
                      </>
                    ) : (
                      <>
                        <span>Sign In</span>
                        <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
                      </>
                    )}
                  </button>

                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-700"></div>
                    </div>
                    <div className="relative flex justify-center">
                      <span className="px-4 text-sm text-gray-400 bg-gray-900">Or continue with</span>
                    </div>
                  </div>

                  {/* Google login */}
                  <div className="flex justify-center transform hover:scale-105 transition-transform duration-300">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      useOneTap
                      shape="circle"
                      size="large"
                      text="signin_with"
                      theme="filled_blue"
                    />
                  </div>

                  {/* Sign up link */}
                  <p className="text-center text-sm text-gray-400 mt-6" hidden>
                    New to BevFlow?{' '}
                    <a href="/auth/register" className="text-purple-400 hover:text-purple-300 font-medium hover:underline transition-colors">
                      Create an account
                    </a>
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-500 text-sm">
            © 2025 BevFlow. All rights reserved. | 
            <a href="#" className="text-gray-400 hover:text-purple-400 ml-2 transition-colors">Privacy Policy</a> | 
            <a href="#" className="text-gray-400 hover:text-purple-400 ml-2 transition-colors">Terms of Service</a>
          </p>
        </div>
      </div>
    </div>
  );
};

const Login = () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "your-google-client-id";
  
  return (
    <GoogleOAuthProvider clientId={clientId}>
      <LoginContent />
    </GoogleOAuthProvider>
  );
};

export default Login;